/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { OPEN_COOKIE_SETTINGS_EVENT } from '@/lib/cookieConsent';

interface SiteLayoutProps {
  children: ReactNode;
}

export function SiteLayout({ children }: SiteLayoutProps) {
  return (
    <div className="min-h-screen bg-white font-sans selection:bg-blue-100 selection:text-blue-900 flex flex-col dark:bg-gray-950 dark:selection:bg-blue-900 dark:selection:text-blue-100">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-md border-b border-gray-100 dark:bg-gray-950/80 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
              <Zap className="text-white w-6 h-6" />
            </div>
            <span className="text-2xl font-black tracking-tighter text-gray-900 dark:text-gray-100">ENDEEKO</span>
          </Link>

          <div className="hidden md:flex items-center gap-8">
            <Link to="/marketplace" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors dark:text-gray-300 dark:hover:text-blue-400">Marketplace</Link>
            <Link to="/companies" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors dark:text-gray-300 dark:hover:text-blue-400">Companies</Link>
            <Link to="/industry/browse" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors dark:text-gray-300 dark:hover:text-blue-400">Browse Industries</Link>
            <Link to="/industry/analytics" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors dark:text-gray-300 dark:hover:text-blue-400">Market Analytics</Link>
            <Link to="/industry/selection" className="text-sm font-medium text-gray-600 hover:text-blue-600 transition-colors dark:text-gray-300 dark:hover:text-blue-400">Pricing</Link>
          </div>

          <div className="flex items-center gap-4">
            <Link to="/login" className="text-sm font-bold text-gray-900 hover:text-blue-600 transition-colors dark:text-gray-100 dark:hover:text-blue-400">Log in</Link>
            <Link
              to="/register"
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-100 dark:shadow-none"
            >
              Get Started
            </Link>
          </div>
        </div>
      </nav>

      {/* Page content */}
      <main className="flex-1 pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-20 border-t border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-20">
            <div className="col-span-1 md:col-span-1">
              <Link to="/" className="flex items-center gap-2 mb-6">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Zap className="text-white w-5 h-5" />
                </div>
                <span className="text-xl font-black tracking-tighter text-gray-900 dark:text-gray-100">ENDEEKO</span>
              </Link>
              <p className="text-gray-500 text-sm leading-relaxed dark:text-gray-400">
                Empowering the next generation of entrepreneurs with data-driven industry insights.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6 uppercase text-xs tracking-widest dark:text-gray-100">Product</h4>
              <ul className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
                <li><Link to="/marketplace" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Marketplace</Link></li>
                <li><Link to="/companies" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Companies</Link></li>
                <li><Link to="/industry/browse" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Browse Industries</Link></li>
                <li><Link to="/industry/analytics" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Market Analytics</Link></li>
                <li><Link to="/industry/selection" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6 uppercase text-xs tracking-widest dark:text-gray-100">Company</h4>
              <ul className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
                <li><Link to="/about" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Careers</Link></li>
                <li><Link to="/contact" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Contact</Link></li>
                <li><Link to="/blog" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Blog</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-6 uppercase text-xs tracking-widest dark:text-gray-100">Legal</h4>
              <ul className="space-y-4 text-sm text-gray-500 dark:text-gray-400">
                <li><Link to="/privacy-policy" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Terms of Service</Link></li>
                <li><Link to="/cookie-policy" className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Cookie Policy</Link></li>
                <li><button type="button" onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT))} className="hover:text-blue-600 transition-colors dark:hover:text-blue-400">Cookie settings</button></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 dark:border-gray-800">
            <div className="text-gray-400 text-xs dark:text-gray-500">
              <p>© 2026 Endeeko Industrial, LLC. All rights reserved.</p>
              <p>Endeeko Industrial, LLC is a subsidiary of Meskeko Inc.</p>
            </div>
            <div className="flex gap-6">
              <div className="w-5 h-5 bg-gray-100 rounded-full dark:bg-gray-800" />
              <div className="w-5 h-5 bg-gray-100 rounded-full dark:bg-gray-800" />
              <div className="w-5 h-5 bg-gray-100 rounded-full dark:bg-gray-800" />
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
